import type {Action} from '@github-ui/action-bar'
import {searchUrl} from '@github-ui/issue-url-helper'
import {ListViewMetadata} from '@github-ui/list-view/ListViewMetadata'
import {useListViewMultiPageSelection} from '@github-ui/list-view/ListViewMultiPageSelectionContext'
import {useListViewSelection} from '@github-ui/list-view/ListViewSelectionContext'
import type {ScopedRepository} from '@github-ui/list-view-items-issues-prs/Query'
import {SortingDropdown} from '@github-ui/list-view-items-issues-prs/SortingDropdown'
/* eslint-disable-next-line @github-ui/github-monorepo/no-use-app-payload --
   useAppPayload is deprecated. See
   https://thehub.github.com/epd/engineering/dev-practicals/frontend/react/data-router/migration/app-payload-deprecation/ */
import {useAppPayload} from '@github-ui/react-core/use-app-payload'
import {useRuntimeEnvironment} from '@github-ui/ssr-utils/use-runtime-environment'
import {Spinner} from '@primer/react'
import {startTransition, useCallback, useDeferredValue, useMemo, useState} from 'react'

import {LABELS} from '../../../constants/labels'
import {usePreviewQueryContext} from '../../../contexts/PreviewQueryContext'
import {
  useActiveSearchQuery,
  useCurrentViewId,
  useDirtySearchQuery,
  useIsQueryLoading,
  useSetDirtySearchQuery,
} from '../../../contexts/QueryContext'
import {useAppNavigate} from '../../../hooks/use-app-navigate'
import type {AppPayload} from '../../../types/app-payload'
import {ListAssigneeFilter} from './ListAssigneeFilter'
import {ListAuthorFilter} from './ListAuthorFilter'
import {ListIssueTypeFilter} from './ListIssueTypeFilter'
import type {ListItemsHeaderProps} from './ListItemsHeader'
import styles from './ListItemsHeaderWithoutBulkActions.module.css'
import {ListLabelFilter} from './ListLabelFilter'
import {ListMilestoneFilter} from './ListMilestoneFilter'
import {ListProjectFilter} from './ListProjectFilter'
import {OpenClosedTabs} from './OpenClosedTabs'

export type FilterBarPickerProps = {
  nested?: boolean
  repo: ScopedRepository
  applySectionFilter: (href: string, url: string) => void
}

export function ListItemsHeaderWithoutBulkActions({
  issueCount,
  issueNodes,
  setCheckedItems,
  setSortingItemSelected,
  setCurrentPage,
  updateListHasPRs,
  issueTypesEnabled,
  searchType,
  ...rest
}: ListItemsHeaderProps) {
  const {scoped_repository, best_match_sort_enabled} = useAppPayload<AppPayload>()
  const dirtySearchQuery = useDirtySearchQuery()
  const deferredDirtySearchQuery = useDeferredValue(dirtySearchQuery)
  const setDirtySearchQuery = useSetDirtySearchQuery()
  const currentViewId = useCurrentViewId()
  const activeSearchQuery = useActiveSearchQuery()
  const isQueryLoading = useIsQueryLoading()
  const {navigateToUrl} = useAppNavigate()
  const previewQuery = usePreviewQueryContext()
  const results = useMemo(() => LABELS.numberOfResults(issueCount), [issueCount])

  // Wrap in startTransition so the filter dropdown can close and paint its
  // final state before React processes the (expensive) list re-render triggered
  // by the URL navigation and dirty-query update. This is the primary source of
  // high INP when the user selects a filter value.
  const applySectionFilter = useCallback(
    (href: string, url: string) => {
      if (previewQuery) {
        startTransition(() => {
          previewQuery.onQueryChange(href)
        })
        return
      }
      startTransition(() => {
        setDirtySearchQuery(href)
        navigateToUrl(url, {preventAutofocus: true})
        setCurrentPage(1)
      })
    },
    [navigateToUrl, previewQuery, setCurrentPage, setDirtySearchQuery],
  )

  const [useSearchQueryForBulk, setUseSearchQueryForBulk] = useState(false)
  const {setSelectedCount} = useListViewSelection()
  const {setMultiPageSelectionAllowed} = useListViewMultiPageSelection()

  const onToggleSelectAll = useCallback(
    (isSelectAllChecked: boolean) => {
      if (isSelectAllChecked) {
        const allItems = issueNodes.reduce<Set<string>>((set, node) => {
          if (node != null) set.add(node.id)
          return set
        }, new Set<string>())
        setCheckedItems(allItems)
        updateListHasPRs(allItems)
      } else {
        setCheckedItems(new Set())
        if (useSearchQueryForBulk) {
          setUseSearchQueryForBulk(false)
          setSelectedCount(0)
          setMultiPageSelectionAllowed?.(false)
        }
      }
    },
    [
      issueNodes,
      setSelectedCount,
      setCheckedItems,
      useSearchQueryForBulk,
      setMultiPageSelectionAllowed,
      updateListHasPRs,
    ],
  )

  const issuesSearchUrl = useCallback((query?: string) => searchUrl({viewId: currentViewId, query}), [currentViewId])
  const sortAction = useMemo(
    () => ({
      key: 'sort-by',
      render: (isOverflowMenu: boolean) => {
        return (
          <SortingDropdown
            activeSearchQuery={activeSearchQuery}
            dirtySearchQuery={deferredDirtySearchQuery || activeSearchQuery}
            setSortingItemSelected={setSortingItemSelected}
            searchUrl={issuesSearchUrl}
            nested={isOverflowMenu}
            setCurrentPage={setCurrentPage}
            bestMatchSortEnabled={best_match_sort_enabled}
            onQueryChange={previewQuery?.onQueryChange}
          />
        )
      },
    }),
    [
      activeSearchQuery,
      deferredDirtySearchQuery,
      issuesSearchUrl,
      setCurrentPage,
      setSortingItemSelected,
      best_match_sort_enabled,
      previewQuery,
    ],
  )

  const spinnerAction = useMemo(
    () => ({
      key: 'spinner',
      render: () => (isQueryLoading ? <Spinner size="small" /> : <></>),
    }),
    [isQueryLoading],
  )

  const filterActions: Action[] = useMemo(() => {
    if (!scoped_repository) {
      return []
    }

    const actionList = [
      {
        key: 'authors',
        render: (isOverflowMenu: boolean) => (
          <ListAuthorFilter nested={isOverflowMenu} repo={scoped_repository} applySectionFilter={applySectionFilter} />
        ),
      },
      {
        key: 'labels',
        render: (isOverflowMenu: boolean) => (
          <ListLabelFilter nested={isOverflowMenu} repo={scoped_repository} applySectionFilter={applySectionFilter} />
        ),
      },
      {
        key: 'projects',
        render: (isOverflowMenu: boolean) => (
          <ListProjectFilter nested={isOverflowMenu} repo={scoped_repository} applySectionFilter={applySectionFilter} />
        ),
      },
      {
        key: 'milestones',
        render: (isOverflowMenu: boolean) => (
          <ListMilestoneFilter
            nested={isOverflowMenu}
            repo={scoped_repository}
            applySectionFilter={applySectionFilter}
          />
        ),
      },
      {
        key: 'assignees',
        render: (isOverflowMenu: boolean) => (
          <ListAssigneeFilter
            nested={isOverflowMenu}
            repo={scoped_repository}
            applySectionFilter={applySectionFilter}
          />
        ),
      },
    ]

    if (issueTypesEnabled) {
      actionList.push({
        key: 'issue-types',
        render: (isOverflowMenu: boolean) => (
          <ListIssueTypeFilter
            nested={isOverflowMenu}
            repo={scoped_repository}
            applySectionFilter={applySectionFilter}
          />
        ),
      })
    }

    return actionList
  }, [applySectionFilter, scoped_repository, issueTypesEnabled])

  const {isBrowser} = useRuntimeEnvironment()

  const allActions = useMemo(
    () => (!isBrowser ? [] : [spinnerAction, ...filterActions, sortAction]),
    [isBrowser, filterActions, sortAction, spinnerAction],
  )
  return (
    <ListViewMetadata
      // For now, adding tabs just for issues#index
      title={!scoped_repository && results}
      sectionFilters={
        scoped_repository && (
          <OpenClosedTabs
            applySectionFilter={applySectionFilter}
            scopedRepository={scoped_repository}
            searchType={searchType}
          />
        )
      }
      onToggleSelectAll={onToggleSelectAll}
      density={'none'}
      assistiveAnnouncement={isQueryLoading ? LABELS.loadingQueryResults : undefined}
      actionsLabel="Actions"
      actions={allActions}
      // actions={[spinnerAction, ...filterActions, sortAction]}
      className={styles.ListViewMetadata_0}
      {...rest}
    />
  )
}
